import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, ExternalLink, Heart } from "lucide-react";
import { api } from "../lib/api";

const TIER_META = {
  or: { label: "Présentateurs · Or", accent: "from-yellow-400 to-amber-500", text: "text-yellow-400", size: "lg" },
  argent: { label: "Partenaires · Argent", accent: "from-zinc-300 to-zinc-500", text: "text-zinc-300", size: "md" },
  bronze: { label: "Amis · Bronze", accent: "from-orange-700 to-amber-800", text: "text-orange-300", size: "sm" },
};

const ORDER = ["or", "argent", "bronze"];

const SponsorCard = ({ sp, size }) => {
  const sizes = {
    lg: "aspect-[5/3] p-8",
    md: "aspect-[5/3] p-6",
    sm: "aspect-[4/3] p-4",
  };
  const Inner = (
    <div className={`relative bg-[#111] border border-white/10 hover:border-[#E60012] transition-all hover:-translate-y-1 ${sizes[size]} flex items-center justify-center group`} data-testid={`sponsor-card-${sp.id}`}>
      {sp.logo_base64 ? (
        <img
          src={sp.logo_base64}
          alt={sp.name}
          className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform"
          style={{ filter: "drop-shadow(0 0 10px rgba(0,0,0,0.4))" }}
        />
      ) : (
        <div className="font-display text-3xl uppercase text-white/80 text-center px-4">{sp.name}</div>
      )}
      {sp.url && (
        <span className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-[#E60012]">
          <ExternalLink className="w-4 h-4" />
        </span>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-2 text-center bg-gradient-to-t from-black/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="text-xs font-condensed uppercase tracking-[0.2em] text-white">{sp.name}</div>
      </div>
    </div>
  );
  if (sp.url) {
    return (
      <a href={sp.url} target="_blank" rel="noopener noreferrer">
        {Inner}
      </a>
    );
  }
  return Inner;
};

const Commanditaires = () => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/sponsors").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const grouped = useMemo(() => {
    if (!items) return null;
    const out = { or: [], argent: [], bronze: [], custom: {} };
    items.forEach((s) => {
      if (s.tier === "custom") {
        const cat = s.category || "Autres";
        out.custom[cat] = out.custom[cat] || [];
        out.custom[cat].push(s);
      } else if (out[s.tier]) {
        out[s.tier].push(s);
      } else {
        // Unknown tier — surface under custom 'Autres' rather than silent bronze coercion
        out.custom["Autres"] = out.custom["Autres"] || [];
        out.custom["Autres"].push(s);
      }
    });
    return out;
  }, [items]);

  return (
    <div className="bg-[#0a0a0a] text-white min-h-screen" data-testid="commanditaires-page">
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 diagonal-stripes opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">
            Merci à eux
          </span>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl lg:text-8xl uppercase leading-none">
            Nos <span className="text-[#E60012]">commanditaires</span>
          </h1>
          <p className="mt-4 text-white/60 max-w-2xl">
            La Coupe CH rayonne grâce au soutien de partenaires passionnés qui croient en la relève.
          </p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 space-y-16">
        {items === null && (
          <div className="text-center py-20 text-white/50 font-condensed uppercase tracking-[0.2em]">
            Chargement…
          </div>
        )}
        {items && items.length === 0 && (
          <div className="bg-[#111] border border-white/10 p-12 text-center">
            <Heart className="w-10 h-10 text-[#E60012] mx-auto mb-3" />
            <div className="font-display text-3xl uppercase">Devenir commanditaire</div>
            <p className="mt-2 text-white/55 max-w-md mx-auto">
              Vous souhaitez associer votre entreprise à la Coupe CH ?
            </p>
            <Link
              to="/devenir-commanditaire"
              data-testid="empty-become-sponsor"
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-[#E60012] hover:bg-[#ff1a30] text-white font-condensed uppercase tracking-[0.2em] text-xs clip-tab"
            >
              Demander un partenariat
            </Link>
          </div>
        )}
        {grouped && ORDER.map((tier) => {
          const list = grouped[tier];
          if (!list || list.length === 0) return null;
          const meta = TIER_META[tier];
          const cols = tier === "or" ? "sm:grid-cols-2 lg:grid-cols-3" : tier === "argent" ? "sm:grid-cols-3 lg:grid-cols-4" : "grid-cols-2 sm:grid-cols-4 lg:grid-cols-6";
          return (
            <div key={tier} data-testid={`tier-${tier}`}>
              <div className="flex items-center gap-3 mb-6">
                <Trophy className={`w-5 h-5 ${meta.text}`} />
                <h2 className="font-display text-3xl sm:text-4xl uppercase">{meta.label}</h2>
                <div className={`flex-1 h-px bg-gradient-to-r ${meta.accent} opacity-40`} />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`grid ${cols} gap-4 sm:gap-6`}
              >
                {list.map((sp) => (
                  <SponsorCard key={sp.id} sp={sp} size={meta.size} />
                ))}
              </motion.div>
            </div>
          );
        })}
        {grouped && Object.entries(grouped.custom).map(([cat, list]) => (
          <div key={cat} data-testid={`tier-custom-${cat.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center gap-3 mb-6">
              <Trophy className="w-5 h-5 text-[#E60012]" />
              <h2 className="font-display text-3xl sm:text-4xl uppercase">{cat}</h2>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4 sm:gap-6">
              {list.map((sp) => (
                <SponsorCard key={sp.id} sp={sp} size="sm" />
              ))}
            </div>
          </div>
        ))}

        {/* CTA Become sponsor */}
        {items && items.length > 0 && (
          <div className="bg-gradient-to-r from-[#1a0306] to-[#0a0a0a] border border-[#E60012]/30 p-10 clip-jersey text-center">
            <span className="font-condensed uppercase tracking-[0.3em] text-xs text-[#E60012]">
              Vous aussi ?
            </span>
            <h3 className="mt-2 font-display text-3xl sm:text-4xl uppercase leading-tight">
              Rejoignez nos <span className="text-[#E60012]">commanditaires</span>
            </h3>
            <Link
              to="/devenir-commanditaire"
              data-testid="cta-become-sponsor"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-[#E60012] hover:bg-[#ff1a30] text-white font-condensed uppercase tracking-[0.2em] text-xs clip-tab"
            >
              Devenir commanditaire
            </Link>
          </div>
        )}
      </section>
    </div>
  );
};

export default Commanditaires;
